import { type UseQueryOptions, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";
import type { RoleResponse } from "~types/role";
import { useApi } from "./useApi";

type TransformFn<T> = (data: RoleResponse) => T;

export const useFetchRoles = <TTransformedData = RoleResponse>(
  customParams: Record<string, string>,
  transformFn: TransformFn<TTransformedData> = (data) => data as unknown as TTransformedData,
  additionalQueryKeys: string[] = [],
  options: Omit<UseQueryOptions<TTransformedData>, "queryKey" | "queryFn"> = {},
) => {
  const request = useApi();
  const { organizationId, sport, seasonId } = useParams();

  if (!organizationId || !sport || !seasonId) {
    throw new Error(
      `OrganizationId, sport or seasonId not found in route params. organizationId: ${organizationId}, sport: ${sport}, seasonId: ${seasonId}`,
    );
  }

  const params = new URLSearchParams({
    seasonId,
    include: ["persons", "seasons", "entities", "entityGroups"].join(","),
    ...customParams,
  });

  return useQuery({
    queryKey: ["roles", sport, organizationId, ...additionalQueryKeys, params.toString()],
    queryFn: () => request<RoleResponse>(`${sport}/o/${organizationId}/roles?${params.toString()}`).then(transformFn),
    ...options,
  });
};
